/**
 * F44 — Visor de documentos habituales sin salir de la app.
 *
 * Pide GET /documents/:id/versions/:n/preview y renderiza según el formato:
 *  - pdf/image → el propio endpoint de descarga con ?inline=1 (iframe / img)
 *  - html (docx convertido con mammoth, xlsx a tablas) → iframe sandbox
 *    (mismo patrón que el visor de correo: sin scripts, origen opaco)
 *  - text (xml/json/csv/md/txt) → <pre>
 *  - none → aviso + botón de descarga
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Download, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import Modal from '../ui/Modal'

interface Preview {
  kind: 'pdf' | 'image' | 'html' | 'text' | 'none'
  html?: string
  text?: string
  truncated?: boolean
}

interface Props {
  documentId: string
  version: number
  fileName: string
  onClose: () => void
}

function buildSrcDoc(html: string): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">' +
    '<style>body{margin:16px;font-family:-apple-system,Segoe UI,sans-serif;font-size:14px;' +
    'color:#0f172a;background:#fff;word-break:break-word}img{max-width:100%;height:auto}' +
    'table{border-collapse:collapse;font-size:12px;margin-bottom:16px}' +
    'td,th{border:1px solid #cbd5e1;padding:3px 8px;white-space:nowrap}' +
    'h3{font-size:13px;margin:12px 0 6px}</style></head><body>' +
    html +
    '</body></html>'
  )
}

export default function DocumentViewerModal({ documentId, version, fileName, onClose }: Props) {
  const { t } = useTranslation(['documents', 'common'])
  const [preview, setPreview] = useState<Preview | null>(null)
  const [error, setError] = useState(false)

  const downloadUrl = `/api/documents/${documentId}/versions/${version}/download`

  useEffect(() => {
    let cancelled = false
    setPreview(null)
    setError(false)
    api
      .get<Preview>(`/documents/${documentId}/versions/${version}/preview`)
      .then((p) => {
        if (!cancelled) setPreview(p)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [documentId, version])

  return (
    <Modal open onClose={onClose} title={fileName} maxWidth="max-w-5xl">
      <div className="h-[72vh] flex flex-col">
        {!preview && !error && (
          <div className="flex-1 grid place-items-center t-muted text-sm">
            <span className="flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> {t('viewer.loading')}
            </span>
          </div>
        )}

        {(error || preview?.kind === 'none') && (
          <div className="flex-1 grid place-items-center">
            <div className="text-center space-y-3">
              <AlertCircle size={24} className="mx-auto t-faint" />
              <p className="t-muted text-sm">{t('viewer.noPreview')}</p>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-toga-600 hover:bg-toga-700"
              >
                <Download size={13} /> {t('common:actions.download')}
              </a>
            </div>
          </div>
        )}

        {preview?.kind === 'pdf' && (
          <iframe title={fileName} src={`${downloadUrl}?inline=1`} className="flex-1 w-full rounded-lg border border-base bg-white" />
        )}

        {preview?.kind === 'image' && (
          <div className="flex-1 overflow-auto grid place-items-center bg-[var(--bg-input-soft)] rounded-lg">
            <img src={`${downloadUrl}?inline=1`} alt={fileName} className="max-w-full" />
          </div>
        )}

        {preview?.kind === 'html' && preview.html != null && (
          <iframe
            title={fileName}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            srcDoc={buildSrcDoc(preview.html)}
            className="flex-1 w-full rounded-lg border border-base bg-white"
          />
        )}

        {preview?.kind === 'text' && (
          <div className="flex-1 overflow-auto rounded-lg border border-base bg-[var(--bg-input)] p-4">
            <pre className="text-xs t-secondary whitespace-pre-wrap break-words">{preview.text}</pre>
            {preview.truncated && (
              <p className="text-[11px] t-faint italic mt-2">{t('viewer.truncated')}</p>
            )}
          </div>
        )}

        <div className="flex justify-end pt-3">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            <Download size={13} /> {t('common:actions.download')}
          </a>
        </div>
      </div>
    </Modal>
  )
}
